import * as THREE from 'three'

import { MeshEntry } from './meshEntry'
import { bleedTransparentRgb } from './textureBleed'

import { FaceDecal, FaceSurfaceText, RobloxFace, Vec3 } from '../types'

/**
 * 単位ボックス（半幅 0.5）の各面へオーバーレイ平面を載せる位置・回転。
 * PlaneGeometry(1,1) は XY 平面・法線 +Z・中心原点なので、面ごとに回転して
 * 法線を外向きへ向け、半幅 0.5 だけ押し出す。container.scale が Part 寸法を与える。
 */
const DECAL_FACE_TRANSFORM: Record<
  RobloxFace,
  { pos: [number, number, number]; rot: [number, number, number] }
> = {
  back: { pos: [0, 0, 0.5], rot: [0, 0, 0] }, // +Z
  front: { pos: [0, 0, -0.5], rot: [0, Math.PI, 0] }, // -Z
  right: { pos: [0.5, 0, 0], rot: [0, Math.PI / 2, 0] }, // +X
  left: { pos: [-0.5, 0, 0], rot: [0, -Math.PI / 2, 0] }, // -X
  top: { pos: [0, 0.5, 0], rot: [-Math.PI / 2, 0, 0] }, // +Y
  bottom: { pos: [0, -0.5, 0], rot: [Math.PI / 2, 0, 0] }, // -Y
}

/** 全デカールオーバーレイで共有する単位平面（解放しない。マテリアルのみ entry 個別）。 */
const DECAL_PLANE_GEOMETRY = new THREE.PlaneGeometry(1, 1)

/** ジオメトリの UV を (repeatU, repeatV) 倍にスケールしてタイル繰り返しを織り込む。 */
const scaleGeometryUv = (
  geometry: THREE.BufferGeometry,
  repeatU: number,
  repeatV: number,
): void => {
  const uv = geometry.attributes.uv
  for (let i = 0; i < uv.count; i += 1) {
    uv.setXY(i, uv.getX(i) * repeatU, uv.getY(i) * repeatV)
  }
  uv.needsUpdate = true
}

/**
 * 単位平面に repeat 込み UV を持たせた個別ジオメトリ。texture.repeat に依存せず UV 側で
 * タイル繰り返しを表現することで、同一画像の Texture を面サイズ違いでも共有できる。
 */
const makeDecalUvGeometry = (
  repeatU: number,
  repeatV: number,
): THREE.BufferGeometry => {
  const geometry = DECAL_PLANE_GEOMETRY.clone()
  scaleGeometryUv(geometry, repeatU, repeatV)
  return geometry
}

/** 面 face における (U=横, V=縦) のワールド寸法。Texture タイルの repeat 算出に使う。 */
const faceUvExtent = (
  face: RobloxFace,
  size: Vec3,
): { u: number; v: number } => {
  switch (face) {
    case 'top':
    case 'bottom':
      return { u: size.x, v: size.z }
    case 'front':
    case 'back':
      return { u: size.x, v: size.y }
    case 'left':
    case 'right':
      return { u: size.z, v: size.y }
  }
}

/**
 * カットアウト用テクスチャの透明テクセル RGB を不透明部分の色で埋める
 * （詳細は textureBleed.ts の bleedTransparentRgb を参照）。
 * 画像を canvas へ展開して処理し、テクスチャの image を差し替える。
 */
export const bleedCutoutTexture = (texture: THREE.Texture): void => {
  const image: unknown = texture.image
  if (!(image instanceof HTMLImageElement)) {
    return
  }
  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight
  const context = canvas.getContext('2d')
  if (!context) {
    return
  }
  // クロスオリジン画像では getImageData が SecurityError を投げ得る。
  // 失敗時は元テクスチャのまま表示を続ける（白にじみは残るが描画は壊さない）。
  try {
    context.drawImage(image, 0, 0)
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
    const bled = bleedTransparentRgb(
      imageData.data,
      canvas.width,
      canvas.height,
    )
    context.putImageData(new ImageData(bled, canvas.width, canvas.height), 0, 0)
  } catch (error) {
    console.warn('カットアウトテクスチャのカラーブリードに失敗しました:', error)
    return
  }
  texture.image = canvas
  texture.needsUpdate = true
}

/**
 * 平面投影 UV（computePlanarUvs）における支配面（最大面積の投影面）の
 * (U, V) ワールド寸法。wedge の Texture タイル repeat の概算に使う。
 * 投影面ごとの UV 軸の対応は computePlanarUvs と同じ
 * （±X→(z,y) / ±Y→(x,z) / ±Z→(x,y)）。
 */
const dominantPlanarUvExtent = (size: Vec3): { u: number; v: number } => {
  const areaX = size.y * size.z
  const areaY = size.x * size.z
  const areaZ = size.x * size.y
  if (areaX >= areaY && areaX >= areaZ) {
    return { u: size.z, v: size.y }
  }
  if (areaY >= areaZ) {
    return { u: size.x, v: size.z }
  }
  return { u: size.x, v: size.y }
}

/**
 * wedge / cornerWedge への Decal/Texture 適用（GAP-DECAL の補完）。
 * 手組みジオメトリには box のような面別オーバーレイ平面を置けないため、
 * 下地とジオメトリを共有するオーバーレイメッシュ 1 枚に、平面投影 UV
 * （buildShapeGeometry が与える 0..1 UV）で画像を貼る近似を取る。
 * - 想定ユース（RJF05 の屋根板）は全 6 面に同じ画像の Texture なので、
 *   画像は先頭の decal の 1 種のみ使う（面別の貼り分けは再現しない）。
 * - タイル繰り返しは支配面（最大面積の投影面）の寸法から概算する。他の面では
 *   タイル密度が多少ずれるが、薄板用途では支配面が見た目を決める。
 * 非同期ロード完了時に entry が破棄/差し替え済み（modelToken 変化）なら捨てる。
 */
const loadWedgeDecalFor = (
  entry: MeshEntry,
  decals: readonly FaceDecal[],
  size: Vec3,
  loadSharedDecalTexture: (
    url: string,
    repeat: boolean,
  ) => Promise<THREE.Texture>,
): void => {
  const decal = decals[0]
  if (!decal) {
    return
  }
  const token = entry.modelToken
  const { studsPerTileU, studsPerTileV } = decal
  const tiled = Boolean(studsPerTileU && studsPerTileV)
  loadSharedDecalTexture(decal.textureUrl, tiled)
    .then((texture) => {
      if (entry.modelToken !== token) {
        // 共有 Texture は破棄しない（他部品が参照中。解放は共有キャッシュで一括）。
        return
      }
      // タイル繰り返しは支配面寸法から UV へ織り込む（texture.repeat に依存せず画像
      // Texture を共有するため）。繰り返し無しなら下地ジオメトリをそのまま共有する。
      let geometry = entry.boxGeometry
      if (studsPerTileU && studsPerTileV) {
        const { u, v } = dominantPlanarUvExtent(size)
        geometry = entry.boxGeometry.clone()
        scaleGeometryUv(
          geometry,
          Math.max(1 / 64, u / studsPerTileU),
          Math.max(1 / 64, v / studsPerTileV),
        )
        entry.decalGeometries.push(geometry)
      }
      // box の面オーバーレイと同じ方針: 白マテリアルに画像を載せ Part 色で乗算しない。
      // 下地と同一形状のため polygonOffset で z ファイティングを避け、巻き順が不定な
      // 手組みジオメトリに合わせて両面表示にする。
      const decalTransparency = decal.transparency ?? 0
      const material = new THREE.MeshStandardMaterial({
        map: texture,
        color: 0xffffff,
        roughness: 1,
        metalness: 0,
        alphaTest: decalTransparency > 0 ? 0.01 : 0.1,
        transparent: decalTransparency > 0,
        opacity: 1 - decalTransparency,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1,
        side: THREE.DoubleSide,
      })
      material.userData.baseTransparent = decalTransparency > 0
      material.userData.baseOpacity = 1 - decalTransparency
      const overlay = new THREE.Mesh(geometry, material)
      overlay.raycast = () => {}
      entry.box.add(overlay)
      entry.decalMaterials.push(material)
    })
    .catch((error) => {
      console.warn(`Failed to load decal texture (${decal.textureUrl}):`, error)
    })
}

/**
 * box の各面へ Decal/Texture 画像をオーバーレイ平面として重ねる（GAP-DECAL）。
 * - 下地の box 面マテリアル（Part 色）はそのまま残し、別の白マテリアル＋平面で画像を
 *   重ねる。これにより画像が Part 色で乗算されず、透明ピクセルでは下地の Part 色が
 *   見える（実機の Decal/Texture 挙動）。
 * - Texture: タイル状に繰り返す（repeat = 面寸法 / StudsPerTile）。
 * - Decal: 面全体に 1 枚（端の伸びを防ぐため clamp）。alphaTest で透明部を抜く。
 * wedge / cornerWedge は loadWedgeDecalFor の全面投影近似で適用する。
 * ball / cylinder は面の概念も全面 UV も無いため適用しない（該当シーンも無い）。
 * 非同期ロード完了時に entry が破棄/差し替え済み（modelToken 変化）なら捨てる。
 */
export const loadDecalsFor = (
  entry: MeshEntry,
  decals: readonly FaceDecal[],
  size: Vec3,
  loadSharedDecalTexture: (
    url: string,
    repeat: boolean,
  ) => Promise<THREE.Texture>,
): void => {
  if (entry.shape === 'wedge' || entry.shape === 'cornerWedge') {
    loadWedgeDecalFor(entry, decals, size, loadSharedDecalTexture)
    return
  }
  if (entry.shape !== 'box') {
    return
  }
  const token = entry.modelToken
  for (const decal of decals) {
    const { studsPerTileU, studsPerTileV } = decal
    const tiled = Boolean(studsPerTileU && studsPerTileV)
    loadSharedDecalTexture(decal.textureUrl, tiled)
      .then((texture) => {
        if (entry.modelToken !== token) {
          // 共有 Texture は破棄しない（他部品が参照中。解放は共有キャッシュで一括）。
          return
        }
        // タイル繰り返しは面寸法から UV へ織り込む（texture.repeat に依存せず画像
        // Texture を共有するため）。繰り返し無し（Decal 1 枚）なら共有平面を使う。
        let geometry: THREE.BufferGeometry = DECAL_PLANE_GEOMETRY
        if (studsPerTileU && studsPerTileV) {
          const { u, v } = faceUvExtent(decal.face, size)
          geometry = makeDecalUvGeometry(
            Math.max(1 / 64, u / studsPerTileU),
            Math.max(1 / 64, v / studsPerTileV),
          )
          entry.decalGeometries.push(geometry)
        }
        // 白マテリアルに画像を載せる（Part 色で乗算しない）。alphaTest で透明部を抜き、
        // polygonOffset で下地の box 面との z ファイティングを避ける。Decal/Texture の
        // Transparency（>0）はアルファブレンドで下地の Part 色へうっすら重ねる
        // （例: RJF07 の床タイルは 0.8 で薄い模様になる）。
        const decalTransparency = decal.transparency ?? 0
        const material = new THREE.MeshStandardMaterial({
          map: texture,
          color: 0xffffff,
          roughness: 1,
          metalness: 0,
          alphaTest: decalTransparency > 0 ? 0.01 : 0.1,
          transparent: decalTransparency > 0,
          opacity: 1 - decalTransparency,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -1,
        })
        // 遮蔽フェード（restoreEntry）が元へ戻すときの基準値。Transparency 付き
        // Decal/Texture を一律 opacity=1 に戻すと固有の透過が失われるため保存する。
        material.userData.baseTransparent = decalTransparency > 0
        material.userData.baseOpacity = 1 - decalTransparency
        const overlay = new THREE.Mesh(geometry, material)
        const transform = DECAL_FACE_TRANSFORM[decal.face]
        overlay.position.set(...transform.pos)
        overlay.rotation.set(...transform.rot)
        // クリック判定は下地の box に通す（オーバーレイ自身は raycast 対象外）。
        overlay.raycast = () => {}
        entry.box.add(overlay)
        entry.decalMaterials.push(material)
      })
      .catch((error) => {
        console.warn(
          `Failed to load decal texture (${decal.textureUrl}):`,
          error,
        )
      })
  }
}

/** テキスト看板 canvas の縦解像度（px）。横は面アスペクトに比例して決める。 */
const SURFACE_TEXT_CANVAS_HEIGHT = 128

/**
 * 面のテキスト看板（SurfaceGui > TextLabel 由来）を CanvasTexture へ焼き込む。
 * 背景色で塗り、文字を中央へ、横幅に収まるようフォントを縮めて描く。
 * canvas 2D が使えない環境（getContext 失敗）では null を返し、貼り付けを諦める。
 */
const createSurfaceTextTexture = (
  surface: FaceSurfaceText,
  aspect: number,
  maxAnisotropy: number,
): THREE.CanvasTexture | null => {
  const height = SURFACE_TEXT_CANVAS_HEIGHT
  const width = Math.max(height, Math.round(height * Math.max(0.1, aspect)))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) {
    console.warn('テキスト看板の canvas 2D コンテキスト取得に失敗しました')
    return null
  }
  if (surface.background !== undefined) {
    context.fillStyle = surface.background
    context.fillRect(0, 0, width, height)
  }
  context.fillStyle = surface.color
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  const paddingX = width * 0.06
  let fontSize = Math.round(height * 0.62)
  context.font = `bold ${fontSize}px sans-serif`
  // 長い文言でも切れないよう、横幅に収まるまでフォントを縮める。
  while (
    fontSize > 8 &&
    context.measureText(surface.text).width > width - paddingX * 2
  ) {
    fontSize -= 2
    context.font = `bold ${fontSize}px sans-serif`
  }
  context.fillText(surface.text, width / 2, height / 2)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = maxAnisotropy
  return texture
}

/**
 * box の各面へ固定テキスト看板（SurfaceGui > TextLabel 由来）をオーバーレイ平面
 * として貼る（例: RJS04 の外野フェンス Score 看板）。Decal/Texture と同じ面配置・
 * polygonOffset・raycast 無効化を用い、生成テクスチャは entry の decal 配列に
 * 載せて遮蔽フェード・破棄の対象にする。box 以外（面の概念が無い）は対象外。
 */
export const loadSurfaceTextFor = (
  entry: MeshEntry,
  surfaceTexts: readonly FaceSurfaceText[],
  size: Vec3,
  maxAnisotropy: number,
): void => {
  if (entry.shape !== 'box') {
    return
  }
  for (const surface of surfaceTexts) {
    const { u, v } = faceUvExtent(surface.face, size)
    const texture = createSurfaceTextTexture(
      surface,
      v > 0 ? u / v : 1,
      maxAnisotropy,
    )
    if (!texture) {
      continue
    }
    // 背景色つき看板は不透明、背景省略（文字だけ）は alphaTest で透明部を抜く。
    const hasBackground = surface.background !== undefined
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      color: 0xffffff,
      roughness: 1,
      metalness: 0,
      transparent: !hasBackground,
      alphaTest: hasBackground ? 0 : 0.5,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    })
    material.userData.baseTransparent = !hasBackground
    material.userData.baseOpacity = 1
    const overlay = new THREE.Mesh(DECAL_PLANE_GEOMETRY, material)
    const transform = DECAL_FACE_TRANSFORM[surface.face]
    overlay.position.set(...transform.pos)
    overlay.rotation.set(...transform.rot)
    // クリック判定は下地の box に通す（オーバーレイ自身は raycast 対象外）。
    overlay.raycast = () => {}
    entry.box.add(overlay)
    entry.decalMaterials.push(material)
    entry.decalTextures.push(texture)
  }
}
